import {useLayoutEffect, useRef} from 'react';

/** Below this a pill stops shrinking and ellipsises instead. */
const MIN_FONT_SIZE = 10;

/** Half-pixel steps after the first guess; from 14px to the floor is eight. */
const MAX_STEPS = 8;

interface Fit {
    element: HTMLElement;
    size: number;
}

/** Labels mounted or resized since the last pass. */
const pending = new Set<HTMLElement>();
let scheduled = false;

/** The labels in each container, so that one observer serves every pill on the page. */
const labelsIn = new Map<Element, Set<HTMLElement>>();
const widthOf = new WeakMap<Element, number>();
let observer: ResizeObserver | null = null;

/**
 * Fits every pending label in one pass: all the writes, then all the reads, then all the writes.
 *
 * A table mounts its rows inside its own scroll handler, and a pill that measured itself on mount forced
 * the browser to lay out the whole table once per pill - resetting its style and then asking for its
 * width, a dozen times over for a dozen rows. The scroll ran ahead of a thread that was busy measuring,
 * into rows that were not drawn yet, and the table flickered empty rows at every screenful. Measured
 * together, a batch costs one layout for the labels that fit and one per half-pixel step for the rest.
 */
const fitPending = () => {
    scheduled = false;
    const elements = Array.from(pending).filter((element) => element.isConnected);
    pending.clear();

    // Writes: undo an earlier fit, and only where there was one, so the label is measured at its own size.
    elements.forEach((element) => {
        if (element.style.fontSize) {
            element.style.fontSize = '';
            element.style.height = '';
            element.style.lineHeight = '';
            element.style.boxSizing = '';
        }
    });

    // Reads.
    const measured = elements.map((element) => {
        if (element.scrollWidth <= element.clientWidth) {
            return null;
        }

        const style = getComputedStyle(element);
        const height = element.getBoundingClientRect().height;
        const inner = height - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)
            - parseFloat(style.borderTopWidth) - parseFloat(style.borderBottomWidth);
        // A first guess from the ratio. The padding does not scale with the text, so the ratio alone
        // leaves it a little wide, and the steps below take it the rest of the way.
        const size = Math.max(MIN_FONT_SIZE,
            Math.floor((parseFloat(style.fontSize) * element.clientWidth / element.scrollWidth) * 2) / 2);

        return {element, height, inner, size};
    });

    // Writes. The box stays as tall as at full size, the smaller text centred in it. Border-box, or the
    // padding would be added on top of a height that already includes it.
    const fits: Fit[] = [];
    measured.forEach((entry) => {
        if (!entry) {
            return;
        }
        entry.element.style.boxSizing = 'border-box';
        entry.element.style.height = `${entry.height}px`;
        entry.element.style.lineHeight = `${entry.inner}px`;
        entry.element.style.fontSize = `${entry.size}px`;
        fits.push({element: entry.element, size: entry.size});
    });

    // Half-pixel steps, every label that is still too wide at once: one layout per step, not per label.
    let wide = fits;
    for (let step = 0; step < MAX_STEPS && wide.length > 0; step += 1) {
        wide = wide.filter((fit) => fit.size > MIN_FONT_SIZE && fit.element.scrollWidth > fit.element.clientWidth);
        wide.forEach((fit) => {
            fit.size = Math.max(MIN_FONT_SIZE, fit.size - 0.5);
            fit.element.style.fontSize = `${fit.size}px`;
        });
    }

    // The full text on the title, for a label that still does not fit at the floor.
    const titles = elements.map((element) =>
        element.scrollWidth > element.clientWidth ? (element.textContent ?? '') : '');
    elements.forEach((element, index) => {
        if (element.title !== titles[index]) {
            element.title = titles[index];
        }
    });
};

/**
 * Before the frame is painted - a microtask runs as soon as the commit, or the scroll handler that
 * committed, is done - so a label is never seen at full size and then shrunk.
 */
const schedule = (element: HTMLElement) => {
    pending.add(element);
    if (!scheduled) {
        scheduled = true;
        queueMicrotask(fitPending);
    }
};

// Refit when a column changes width, and only then: resizing the label can change the container's
// height, and reacting to that would be a loop. The width comes with the entry, so reading it costs
// no layout; the first report of a container is the size it was fitted at.
const observe = (element: HTMLElement, container: Element) => {
    observer ??= new ResizeObserver((entries) => {
        entries.forEach((entry) => {
            const width = entry.contentRect.width;
            const known = widthOf.get(entry.target);
            widthOf.set(entry.target, width);
            if (known === undefined || known === width) {
                return;
            }
            labelsIn.get(entry.target)?.forEach(schedule);
        });
    });

    const labels = labelsIn.get(container);
    if (labels) {
        labels.add(element);
        return;
    }
    labelsIn.set(container, new Set([element]));
    observer.observe(container);
};

const forget = (element: HTMLElement, container: Element) => {
    pending.delete(element);
    const labels = labelsIn.get(container);
    labels?.delete(element);
    if (labels && labels.size === 0) {
        labelsIn.delete(container);
        observer?.unobserve(container);
    }
};

/**
 * Shrinks a one-line label until it fits the room it has, down to a floor, and ellipsises past it.
 *
 * Status and type pills are laid out for their English text; the French or German for the same value
 * can be half as long again, and a table cell will not grow to hold it. A second line kept the letters
 * but broke the row; this keeps one line and gives up size before it gives up letters. The pill keeps
 * the height it has at full size, so a shrunk label sits in the same box as its neighbours. The full
 * text goes on the element's title for a label that still does not fit at the floor.
 *
 * `text` is whatever changes what the label says - its key and the language - so a switch of language
 * refits it.
 */
export const useFitText = <T extends HTMLElement>(text: string) => {
    const ref = useRef<T>(null);

    useLayoutEffect(() => {
        const element = ref.current;
        const container = element?.parentElement;
        if (!element || !container) {
            return undefined;
        }

        observe(element, container);
        schedule(element);

        return () => forget(element, container);
    }, [text]);

    return ref;
};
